import { BorderTable } from '@/components/BorderTable';
import { LoadingSkeletonList } from '@/components/SkeletonLoading';
import {
  SKELETON_GAP_XS,
  SKELETON_HEIGHT_XS,
} from '@/components/SkeletonLoading/constants';
import { useGetTransactionItems } from '@/context/services/transaction_items/List/TransactionItems.service';
import { DetailDrawer } from '@/features/transaction_items/components/DetailDrawer/DetailDrawer';
import { useDeleteConfirm } from '@/features/transaction_items/components/Drawer/hooks/useDeleteConfirm';
import { TransactionItemDrawer } from '@/features/transaction_items/components/Drawer/TransactionItemDrawer';
import {
  TransactionItemGeneralForm,
  Props as TransactionItemGeneralFormProps,
} from '@/features/transaction_items/components/Form/GeneralForm/TransactionItemGeneralForm';
import { useSubmitUpdateReorderTransactionItem } from '@/features/transaction_items/components/Form/hooks/useSubmitUpdateReorderTransactionItem';
import { ReorderableTable } from '@/features/transaction_items/List/ReorderableTable/ReorderableTable';
import { useTransactionItemsColumns } from '@/features/transaction_items/List/Table/Column/column';
import { useSubmitDeleteTransactionItem } from '@/features/transaction_items/List/Table/hooks/useDeleteTransactionItem';
import {
  MAX_CUSTOM_TRANSACTION_ITEMS_COUNT,
  TableAction,
} from '@/features/transaction_items/List/Table/TableAction/TableAction';
import {
  TransactionItemDrawerMode,
  TransactionItemDrawerModeEnum,
  TransactionItemsColumn,
} from '@/features/transaction_items/List/type';
import { countVisibleCustomTransactionItems } from '@/features/transaction_items/utils/countVisibleCustomTransactionItems';
import {
  ModernPaginationProps,
  TableProps,
  useToastAPI,
} from '@moneyforward/ap-frontend-components';
import {
  Dispatch,
  FC,
  memo,
  ReactNode,
  SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useParams } from 'react-router-dom';

export type Props = {
  table: TransactionItemsTableProps;
  onDetailClick: (id: string, type: TransactionItemsColumn['type']) => void;
  customTransactionItemsCount?: number;
  hasFlexibleValidationItems?: boolean;
};

export type TransactionItemsViewTableProps = {
  isActiveCustomItemCountLimitEnabled: boolean;
  tableBodyHeight: number;
  isReorderingMode: boolean;
  enterReorderingMode: () => void;
  exitReorderingMode: () => void;
  showFullScreenLoading: (loading: boolean) => void;
  isFullScreenLoading?: boolean;
  onOrderChange: (isOrderChanged: boolean) => void;
  isOrderChanged: boolean;
  confirmCancelReordering: () => Promise<boolean>;
  setIsDrawerIsOpen: Dispatch<SetStateAction<boolean>>;
  notificationErrorContextHolder: ReactNode;
  notificationErrorVisible: boolean;
  onClearNotificationErrorMessage: () => void;
};

export type TransactionItemsTableProps = Omit<
  TableProps<TransactionItemsColumn>,
  'columns' | 'emptyType' | 'pagination'
>;

export const TransactionItemsTable: FC<Props> = memo(
  ({
    table,
    onDetailClick,
    customTransactionItemsCount,
    hasFlexibleValidationItems,
  }) => {
    const columns = useTransactionItemsColumns({
      onDetailClick,
      customTransactionItemsCount,
      hasFlexibleValidationItems,
    });
    const pagination: ModernPaginationProps = useMemo(() => {
      return {
        currentPage: 1,
        maxPage: 1,
        showPerPage: true,
      };
    }, []);
    return (
      <BorderTable
        {...table}
        columns={columns}
        legacy={false}
        pagination={pagination}
        disableRowStyling
        verticalAlign='middle'
      />
    );
  }
);
TransactionItemsTable.displayName = 'TransactionItemsTable';

export const InnerTable: FC<TransactionItemsViewTableProps> = memo(
  ({
    tableBodyHeight,
    isReorderingMode,
    enterReorderingMode,
    exitReorderingMode,
    showFullScreenLoading,
    isFullScreenLoading,
    onOrderChange,
    isOrderChanged,
    confirmCancelReordering,
    setIsDrawerIsOpen,
    notificationErrorContextHolder,
    notificationErrorVisible,
    onClearNotificationErrorMessage,
    isActiveCustomItemCountLimitEnabled,
  }) => {
    const { report_type_id } = useParams();
    const { data, isLoadingGetTransactionItems, refetch } =
      useGetTransactionItems({
        report_type_id: report_type_id ?? '',
      });
    const notify = useToastAPI();
    const sortPriority = useCallback(
      (data: TransactionItemsColumn[]) =>
        [...data].sort((a, b) => a.priority - b.priority),
      []
    );
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [mode, setMode] = useState<TransactionItemDrawerMode>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDetailId, setSelectedDetailId] = useState<
      string | undefined
    >(undefined);
    const [selectedDetailType, setSelectedDetailType] = useState<
      TransactionItemsColumn['type'] | undefined
    >(undefined);
    const [isDetailLoading, setIsDetailLoading] = useState(false);
    const [isShowUpdateConfirm, setIsShowUpdateConfirm] = useState(false);
    const [visibleCountServerErrorMessage, setVisibleCountServerErrorMessage] =
      useState<string | null>(null);
    // reordered list
    const [dataSource, setDataSource] = useState<TransactionItemsColumn[]>(() =>
      sortPriority(data)
    );

    const tbodyStyle = useMemo(() => {
      return {
        height: tableBodyHeight,
      };
    }, [tableBodyHeight]);

    const customTransactionItemsCount = useMemo(() => {
      if (!isActiveCustomItemCountLimitEnabled) {
        return data.filter((item) => item.type === 'custom').length;
      }
      return countVisibleCustomTransactionItems(data);
    }, [data, isActiveCustomItemCountLimitEnabled]);

    const hasFlexibleValidationItems = useMemo(() => {
      return data.some((item) => item.type === 'basic' && item.allows_update);
    }, [data]);

    const openDrawer = useCallback(() => {
      setIsDrawerOpen(true);
      setIsDrawerIsOpen(true);
    }, [setIsDrawerIsOpen]);

    const closeDrawer = useCallback(() => {
      if (notificationErrorVisible) {
        onClearNotificationErrorMessage();
      }
      setVisibleCountServerErrorMessage(null);
      setIsDrawerOpen(false);
      setIsDrawerIsOpen(false);
    }, [
      setIsDrawerIsOpen,
      onClearNotificationErrorMessage,
      notificationErrorVisible,
    ]);
    const refreshDataSource = useCallback(async () => {
      const data = await refetch();
      setDataSource(sortPriority(data));
    }, [refetch, sortPriority]);

    const resetDataSource = useCallback(() => {
      setDataSource(sortPriority(data));
    }, [data, sortPriority]);

    const handlerSuccess = useCallback(
      (successMsg: string) => {
        notify.success({
          title: successMsg,
          duration: 5,
        });
      },
      [notify]
    );

    const handlerError = useCallback(
      (errorMsg: string) => {
        notify.error({
          title: errorMsg,
          duration: 5,
        });
      },
      [notify]
    );

    const { onSubmitReorder } = useSubmitUpdateReorderTransactionItem({
      handlerSuccess,
      handlerError,
      showLoading: showFullScreenLoading,
    });

    const onCancelOrdering = useCallback(async () => {
      if (isOrderChanged) {
        const isConfirmed = await confirmCancelReordering();
        if (isConfirmed) {
          exitReorderingMode();
          resetDataSource();
        }
      } else {
        exitReorderingMode();
      }
    }, [
      isOrderChanged,
      resetDataSource,
      confirmCancelReordering,
      exitReorderingMode,
    ]);

    const selectedItem = useMemo(() => {
      if (!selectedDetailId) return undefined;
      return data.find((item) => item.id === selectedDetailId);
    }, [data, selectedDetailId]);

    const table: TransactionItemsTableProps = useMemo(() => {
      const sortedData = sortPriority(data);
      return {
        data: sortedData,
        scroll: sortedData.length > 0 ? { y: tableBodyHeight } : undefined,
        components:
          sortedData.length === 0
            ? {
                body: {
                  wrapper: ({ children }: { children: React.ReactNode }) => (
                    <tbody style={tbodyStyle}>{children}</tbody>
                  ),
                },
              }
            : undefined,
        tableAction: (
          <TableAction
            customTransactionItemsCount={customTransactionItemsCount}
            onCreate={() => {
              setSelectedDetailId(undefined);
              setSelectedDetailType(undefined);
              openDrawer();
              setMode(TransactionItemDrawerModeEnum.CREATE);
            }}
            isDisabled={
              !isActiveCustomItemCountLimitEnabled &&
              customTransactionItemsCount >= MAX_CUSTOM_TRANSACTION_ITEMS_COUNT
            }
            isActiveCustomItemCountLimitEnabled={
              isActiveCustomItemCountLimitEnabled
            }
            onReordering={enterReorderingMode}
            isReorderingMode={isReorderingMode}
            onCancelOrdering={() => {
              onCancelOrdering();
            }}
            onSubmitReorder={() => {
              onSubmitReorder(dataSource);
              exitReorderingMode();
            }}
            isSubmitDisabled={!isOrderChanged}
          />
        ),
      };
    }, [
      sortPriority,
      data,
      tableBodyHeight,
      customTransactionItemsCount,
      enterReorderingMode,
      isReorderingMode,
      isOrderChanged,
      tbodyStyle,
      openDrawer,
      onCancelOrdering,
      onSubmitReorder,
      dataSource,
      exitReorderingMode,
      isActiveCustomItemCountLimitEnabled,
    ]);

    const showLoading = useCallback((loading: boolean) => {
      setIsLoading(loading);
    }, []);

    useEffect(() => {
      if (onOrderChange) {
        const sortedData = sortPriority(data);
        const isOrderChangedValue =
          dataSource.length > 0 &&
          dataSource.some((item, index) => item.id !== sortedData[index]?.id);
        onOrderChange(isOrderChangedValue);
      }
    }, [data, dataSource, onOrderChange, sortPriority]);

    const onDetailClick = useCallback(
      (id: string, type: TransactionItemsColumn['type']) => {
        setSelectedDetailId(id);
        setSelectedDetailType(type);
        openDrawer();
        setMode(TransactionItemDrawerModeEnum.DETAIL);
      },
      [openDrawer, setMode, setSelectedDetailId, setSelectedDetailType]
    );

    const onSetMode = useCallback(
      (mode: TransactionItemDrawerMode) => {
        setMode(mode);
      },
      [setMode]
    );

    const onShowUpdateConfirm = useCallback((show: boolean) => {
      setIsShowUpdateConfirm(show);
    }, []);

    const onVisibleCountServerError = useCallback(
      (message: string) => {
        if (!isActiveCustomItemCountLimitEnabled) return;
        setVisibleCountServerErrorMessage(message);
      },
      [isActiveCustomItemCountLimitEnabled]
    );

    const onClearVisibleCountServerError = useCallback(() => {
      if (!isActiveCustomItemCountLimitEnabled) return;
      setVisibleCountServerErrorMessage(null);
    }, [isActiveCustomItemCountLimitEnabled]);

    const generalFormProps: TransactionItemGeneralFormProps = useMemo(() => {
      return {
        mode: mode,
        showLoading: showLoading,
        isLoading: isLoading,
        closeDrawer: closeDrawer,
        onShowUpdateConfirm: onShowUpdateConfirm,
        onSetMode: onSetMode,
        handleSuccess: refreshDataSource,
        notificationErrorContextHolder: notificationErrorContextHolder,
        notificationErrorVisible: notificationErrorVisible,
        isActiveCustomItemCountLimitEnabled,
        visibleCustomItemsInUseCount: isActiveCustomItemCountLimitEnabled
          ? customTransactionItemsCount
          : undefined,
        visibleCountServerErrorMessage: isActiveCustomItemCountLimitEnabled
          ? visibleCountServerErrorMessage
          : null,
        onVisibleCountServerError: isActiveCustomItemCountLimitEnabled
          ? onVisibleCountServerError
          : undefined,
        onClearVisibleCountServerError: isActiveCustomItemCountLimitEnabled
          ? onClearVisibleCountServerError
          : undefined,
      };
    }, [
      mode,
      showLoading,
      isLoading,
      closeDrawer,
      onShowUpdateConfirm,
      onSetMode,
      refreshDataSource,
      notificationErrorContextHolder,
      notificationErrorVisible,
      isActiveCustomItemCountLimitEnabled,
      customTransactionItemsCount,
      visibleCountServerErrorMessage,
      onVisibleCountServerError,
      onClearVisibleCountServerError,
    ]);

    const { confirm, ConfirmModal } = useDeleteConfirm();
    const { onDelete } = useSubmitDeleteTransactionItem({
      transaction_item_id: selectedDetailId ?? '',
      handlerSuccess: () => {
        closeDrawer();
      },
      showLoading: showLoading,
    });

    const onDeleteClick = useCallback(async () => {
      const isConfirmed = await confirm();
      if (isConfirmed) {
        onDelete();
      }
    }, [confirm, onDelete]);

    return (
      <>
        {isReorderingMode ? (
          <>
            <ReorderableTable
              table={table}
              dataSource={dataSource}
              setDataSource={setDataSource}
            />
          </>
        ) : (
          <>
            {isFullScreenLoading || isLoadingGetTransactionItems ? (
              // Hide layout shift
              <FallbackTable
                isActiveCustomItemCountLimitEnabled={
                  isActiveCustomItemCountLimitEnabled
                }
                tableBodyHeight={tableBodyHeight}
                isReorderingMode={isReorderingMode}
                enterReorderingMode={enterReorderingMode}
                exitReorderingMode={exitReorderingMode}
                showFullScreenLoading={showFullScreenLoading}
                emptyType='loading'
                onOrderChange={() => {}}
                isOrderChanged={false}
                confirmCancelReordering={() => Promise.resolve(false)}
                setIsDrawerIsOpen={setIsDrawerIsOpen}
                notificationErrorContextHolder={notificationErrorContextHolder}
                notificationErrorVisible={notificationErrorVisible}
                onClearNotificationErrorMessage={
                  onClearNotificationErrorMessage
                }
              />
            ) : (
              <TransactionItemsTable
                table={table}
                onDetailClick={onDetailClick}
                customTransactionItemsCount={customTransactionItemsCount}
                hasFlexibleValidationItems={hasFlexibleValidationItems}
              />
            )}
            <TransactionItemDrawer
              visible={isDrawerOpen}
              onClose={closeDrawer}
              mode={mode}
              type={selectedItem?.type}
              isLoading={isLoading}
              onSetMode={onSetMode}
              isDetailLoading={isDetailLoading}
              isShowUpdateConfirm={isShowUpdateConfirm}
              onDeleteClick={onDeleteClick}
              canDelete={selectedItem?.can_delete}
              deleteTooltip={selectedItem?.delete_tooltip}
              isSubmitDisabled={
                isActiveCustomItemCountLimitEnabled &&
                !!visibleCountServerErrorMessage
              }
            >
              {mode === TransactionItemDrawerModeEnum.CREATE ? (
                <TransactionItemGeneralForm {...generalFormProps} />
              ) : (
                selectedDetailId && (
                  <DetailDrawer
                    {...generalFormProps}
                    selectedDetailId={selectedDetailId}
                    type={selectedDetailType}
                    setIsDetailLoading={setIsDetailLoading}
                  />
                )
              )}
            </TransactionItemDrawer>
            <ConfirmModal />
          </>
        )}
      </>
    );
  }
);
InnerTable.displayName = 'InnerTable';

export const FallbackTable: FC<
  TransactionItemsViewTableProps & {
    emptyType: 'loading' | 'error';
  }
> = memo(({ tableBodyHeight, emptyType }) => {
  const repeatCount = useMemo(
    () =>
      Math.floor(
        (tableBodyHeight - 24) / (SKELETON_HEIGHT_XS + SKELETON_GAP_XS)
      ),
    [tableBodyHeight]
  );

  const tbodyStyle = useMemo(() => {
    return {
      height: tableBodyHeight,
    };
  }, [tableBodyHeight]);

  const tableProps: Omit<TransactionItemsTableProps, 'scroll'> = useMemo(() => {
    return {
      data: [],
      emptyText:
        emptyType === 'loading' ? (
          <LoadingSkeletonList repeatCount={repeatCount} />
        ) : (
          <></>
        ),
      emptyType,
      legacy: false,
      hideFooterPagination: true,
      hideHeaderPagination: true,
      tableAction: <TableAction isLoading={emptyType === 'loading'} />,
      components:
        emptyType === 'error'
          ? {
              body: {
                wrapper: ({ children }: { children: React.ReactNode }) => (
                  <tbody style={tbodyStyle}>{children}</tbody>
                ),
              },
            }
          : undefined,
    };
  }, [emptyType, repeatCount, tbodyStyle]);

  return <TransactionItemsTable table={tableProps} onDetailClick={() => {}} />;
});
FallbackTable.displayName = 'FallbackTable';

export const TransactionItemsTableView: FC<TransactionItemsViewTableProps> =
  memo((props) => {
    return (
      <ErrorBoundary fallback={<FallbackTable {...props} emptyType='error' />}>
        <Suspense fallback={<FallbackTable {...props} emptyType='loading' />}>
          <InnerTable {...props} />
        </Suspense>
      </ErrorBoundary>
    );
  });
TransactionItemsTableView.displayName = 'TransactionItemsTableView';
